import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Comment } from '../types/comment';

interface CommentItemProps {
  comment: Comment;
  currentUserId?: string;
  onLike: (commentId: string) => Promise<void>;
  onEdit: (commentId: string, content: string) => Promise<void>;
  onDelete: (commentId: string) => Promise<void>;
}

export function CommentItem({
  comment,
  currentUserId,
  onLike,
  onEdit,
  onDelete
}: CommentItemProps) {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const isOwner = currentUserId === comment.userId;
  const isLiked = comment.likedBy?.includes(currentUserId || '') || false;

  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) return '';

    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString();
  };

  const handleEdit = async () => {
    if (!editContent.trim() || editContent === comment.content) {
      setIsEditing(false);
      return;
    }

    try {
      setIsSubmitting(true);
      await onEdit(comment.id!, editContent.trim());
      setIsEditing(false);
    } catch (error) {
      console.error('Error editing comment:', error);
      alert('Failed to edit comment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsSubmitting(true);
      await onDelete(comment.id!);
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error('Error deleting comment:', error);
      alert('Failed to delete comment');
      setIsSubmitting(false);
    }
  };

  const handleLike = async () => {
    if (!currentUserId) return;
    try {
      await onLike(comment.id!);
    } catch (error) {
      console.error('Error liking comment:', error);
    }
  };

  return (
    <div className="flex gap-3 group">
      {/* User Avatar */}
      <button
        onClick={() => navigate(`/users/${comment.userId}`)}
        className="flex-shrink-0"
      >
        {comment.userAvatar ? (
          <img
            src={comment.userAvatar}
            alt={comment.userName}
            className="w-8 h-8 rounded-full object-cover"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-semibold text-sm">
            {comment.userName.charAt(0).toUpperCase()}
          </div>
        )}
      </button>

      {/* Comment Content */}
      <div className="flex-1 min-w-0">
        <div className="bg-gray-50 rounded-2xl px-3 py-2">
          <button
            onClick={() => navigate(`/users/${comment.userId}`)}
            className="font-semibold text-gray-900 text-sm hover:underline"
          >
            {comment.userName}
          </button>

          {isEditing ? (
            <div className="mt-1">
              <textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary-500"
                rows={2}
                autoFocus
              />
              <div className="flex gap-2 mt-2">
                <button
                  onClick={handleEdit}
                  disabled={isSubmitting || !editContent.trim()}
                  className="text-xs font-medium text-primary-600 hover:text-primary-700 disabled:opacity-50"
                >
                  Save
                </button>
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setEditContent(comment.content);
                  }}
                  disabled={isSubmitting}
                  className="text-xs font-medium text-gray-600 hover:text-gray-700 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-700 mt-0.5 whitespace-pre-wrap break-words">
              {comment.content}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-4 mt-1 px-3">
          <span className="text-xs text-gray-500">
            {formatTimestamp(comment.createdAt)}
            {(() => {
              // Check if comment was edited by comparing timestamps
              if (!comment.updatedAt || !comment.createdAt) return null;
              const createdTime = comment.createdAt.toMillis ? comment.createdAt.toMillis() : comment.createdAt.toDate().getTime();
              const updatedTime = comment.updatedAt.toMillis ? comment.updatedAt.toMillis() : comment.updatedAt.toDate().getTime();
              // Consider edited if updated more than 1 second after created
              if (updatedTime - createdTime > 1000) {
                return <span className="ml-1">(edited)</span>;
              }
              return null;
            })()}
          </span>

          {currentUserId && (
            <button
              onClick={handleLike}
              className="flex items-center gap-1.5 group"
            >
              <svg
                className={`w-4 h-4 transition-all ${
                  isLiked
                    ? 'text-red-600 fill-current'
                    : 'text-gray-400 group-hover:text-red-600'
                }`}
                fill={isLiked ? 'currentColor' : 'none'}
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                />
              </svg>
              {comment.likesCount && comment.likesCount > 0 && (
                <span className={`text-xs font-medium ${
                  isLiked ? 'text-red-600' : 'text-gray-600'
                }`}>
                  {comment.likesCount}
                </span>
              )}
            </button>
          )}

          {isOwner && !isEditing && (
            <>
              <button
                onClick={() => setIsEditing(true)}
                className="text-xs font-medium text-gray-600 hover:text-gray-900"
              >
                Edit
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="text-xs font-medium text-red-600 hover:text-red-700"
              >
                Delete
              </button>
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Comment</h3>
            <p className="text-gray-600 text-sm mb-6">
              Are you sure you want to delete this comment? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isSubmitting}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={isSubmitting}
                className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {isSubmitting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
